import { useContext } from "react";

import { AppConfigContext } from "../../../core/configuration/Configuration";
import { useAuthorizedFetch } from "../../../core/data-access/useAuthorizedFetch";
import { cacheRequests } from "../../utils/cacheRequests";
import { DubbingsResponse } from "./DubbingsResponse";
import { DynamicSubtitlesResponse } from "./DynamicSubtitlesResponse";
import { EmbeddedSubtitlesResponse } from "./EmbeddedSubtitlesResponse";
import { GetAirlinesLookupResponse } from "./GetAirlinesLookupResponse";
import { GetLanguagesLookupResponse } from "./GetLanguagesLookupResponse";

const languagesCache = cacheRequests<GetLanguagesLookupResponse>();
const airlinesCache = cacheRequests<GetAirlinesLookupResponse>();
const dynamicSubtitlesCache = cacheRequests<DynamicSubtitlesResponse>();
const embeddedSubtitlesCache = cacheRequests<EmbeddedSubtitlesResponse>();
const dubbingsCache = cacheRequests<DubbingsResponse>();

export const useCachedAdminApi = () => {
    const { httpGet } = useAuthorizedFetch();

    const appConfigContext = useContext(AppConfigContext);
    const baseUrl = `${appConfigContext.adminConfig.apiUrl}`;

    const getLanguagesLookup = async (): Promise<GetLanguagesLookupResponse> =>
        languagesCache.getRequest({
            requestFn: () => httpGet<GetLanguagesLookupResponse>(`${baseUrl}languages/get-lookup`),
            key: "languages",
            ttlMs: 20000
        });

    const getEmbeddedSubtitles = async (): Promise<EmbeddedSubtitlesResponse> => {
        return embeddedSubtitlesCache.getRequest({
            requestFn: () => httpGet<EmbeddedSubtitlesResponse>(`${baseUrl}languages/embedded-subs-lookup`),
            key: "embedded-subtitles"
        });
    };

    const getDynamicSubtitles = async (): Promise<DynamicSubtitlesResponse> => {
        return dynamicSubtitlesCache.getRequest({
            requestFn: () => httpGet<DynamicSubtitlesResponse>(`${baseUrl}languages/dynamic-subs-lookup`),
            key: "dynamic-subtitles"
        });
    };

    const getDubbings = async (): Promise<DubbingsResponse> => {
        return dubbingsCache.getRequest({
            requestFn: () => httpGet<DubbingsResponse>(`${baseUrl}languages/dubbings-lookup`),
            key: "dubbings"
        });
    };

    const getAirlinesLookup = async (): Promise<GetAirlinesLookupResponse> =>
        airlinesCache.getRequest({
            requestFn: () => httpGet<GetLanguagesLookupResponse>(`${baseUrl}airlines/get-lookup`),
            key: "airlines",
            ttlMs: 20000
        });

    return {
        getLanguagesLookup,
        getAirlinesLookup,
        getEmbeddedSubtitles,
        getDynamicSubtitles,
        getDubbings
    };
};
